import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { FieldConfig, FormArray, FormGenerator, FormGroup, Handler, InputType } from 'react-reactive-form';
import { useSelector } from 'react-redux';
import { selectDeficiencyCodesByStandardID } from '../../reducers/manageJobCommentsReducer';
import { IinitialState } from '../../reducers';
import { initialFacility } from '../../reducers/initialState';
import { useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { getIsMobile } from '../../reducers/configReducer';
import NoteIcon from '../../images/note-icon.svg';
import CommonModal from '../common/CommonModal';
import { FormUtil } from '../common/FormUtil';
import RTE from '../common/TinyMCERichTextEditor';
import CommonMobileModal from '../common/CommonMobileModal';
import { Ioption } from '../../models';
import InlinePhotoControl, { InlinePhotoControlHandle } from '../photos/InlinePhotoControl';
import { getSelectedJob } from '../../reducers/manageJobReducer';
import { selectPhotosByMeasurementPoint } from '../../reducers/photosReducer';

interface IProps {
    meta: { [key: string]: any };
    handler: (inputType?: InputType, value?: string) => Handler;
}

const NewMPNoteButton = ({ meta, handler }: IProps) => {
    const isMobile = useSelector(getIsMobile);
    const selectedJob = useSelector(getSelectedJob);
    const facility = useSelector(
        (state: IinitialState) =>
            state.facilities.facilitiesByID[state.manageJob.selectedJob.facilityID] || initialFacility
    );
    const codeOptions = useSelector((state: IinitialState) =>
        selectDeficiencyCodesByStandardID(state.manageJobComment.deficiencyCodesByID, facility.standardID)
    );

    const photoRef = useRef<InlinePhotoControlHandle>(null);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [form, setForm] = useState<FormGroup | FormArray>();
    const [content, setContent] = useState<string>('');

    const existingPhotos = useSelector((state: IinitialState) =>
        meta.measurementPointID
            ? selectPhotosByMeasurementPoint(state, { measurementPointID: meta.measurementPointID, installBaseID: meta.installBaseID })
            : []
    );

    const Modal = useMemo(() => (isMobile ? CommonMobileModal : CommonModal), [isMobile]);
    const hasContent = (handler().value && handler().value.length) || existingPhotos.length > 0;
    const buttonStyle = hasContent ? 'warning' : 'default';

    const [fieldConfig] = useState<FieldConfig>({
        controls: {
            codeID: {
                render: FormUtil.Select,
                meta: {
                    label: 'Standard Text',
                    colWidth: 12,
                    isMulti: false,
                    name: 'code',
                    required: false,
                    options: FormUtil.convertToOptions(codeOptions)
                },
                formState: {
                    value: undefined,
                    disabled: false
                }
            }
        }
    });

    useLayoutEffect(() => {
        if (showModal) {
            setContent(handler().value || '');
        }
    }, [showModal, handler]);

    const onMount = (mountForm: FormGroup | FormArray) => {
        setForm(mountForm);
    };

    useEffect(() => {
        if (!form || !showModal) return;

        const onChange = (value: Ioption | undefined) => {
            // Handle case where value is undefined (when form control is reset)
            if (!value || !value.value) {
                setContent('');
                return;
            }

            const { description: html } = codeOptions[value.value] ?? { description: '' };
            setContent(html);
        };

        // Follow the existing pattern in the codebase - just subscribe without trying to unsubscribe
        // The existing MeasurementPointNoteButton.tsx also doesn't handle unsubscription
        const control = form.get('codeID');
        if (control && control.valueChanges) {
            control.valueChanges.subscribe(onChange);
        }
    }, [form, showModal, codeOptions]);

    const save = () => {
        handler().onChange(content);
        photoRef.current?.commitChanges();
        setShowModal(false);
        setContent('');
    };

    const close = () => {
        photoRef.current?.resetChanges();
        setShowModal(false);
        setContent('');
        // Reset form control when closing
        if (form) {
            const control = form.get('codeID');
            if (control) {
                control.patchValue(undefined);
            }
        }
    };

    /*
     * A historical note is there to be read: the visit was recorded on a closed job, so the only
     * button is the way back out. Save was already hidden; Reset selection staged a wipe of the
     * note and its photos that Cancel then had to undo, and the standard-text picker rewrote the
     * note body (MMG-4345).
     */
    const footer = (
        <Col xs={12} className="form-buttons text-right" style={{ padding: '12px 16px' }}>
            <Button variant="default" type="button" className="pull-left" onClick={close}>
                {meta.isHistoricalNote ? meta.t('common:close') : meta.t('common:cancel')}
            </Button>
            {!meta.isHistoricalNote && (
                <>
                    <Button
                        variant="default"
                        type="button"
                        className="cancel-button"
                        style={{ marginLeft: '10px' }}
                        onClick={() => {
                            setContent('');
                            photoRef.current?.clearPhotos();
                            if (form) {
                                const control = form.get('codeID');
                                if (control) {
                                    control.patchValue(undefined);
                                }
                            }
                        }}
                    >
                        {meta.t('mobile:resetSelection')}
                    </Button>
                    <Button variant="primary" type="button" onClick={save}>
                        {meta.t('common:save')}
                    </Button>
                </>
            )}
        </Col>
    );

    return (
        <>
            <Col xs={2} className="mp-row">
                <Button
                    type="button"
                    variant={buttonStyle}
                    onClick={() => {
                        setShowModal(true);
                    }}
                    className="pull-right"
                    style={
                        isMobile
                            ? {
                                  marginTop: '20px',
                                  width: '100%',
                                  height: '36px',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center'
                              }
                            : meta.center
                            ? { marginLeft: '45px' }
                            : { marginTop: '25px' }
                    }
                >
                    {isMobile ? <img src={NoteIcon} alt="" /> : meta.t('notes')}
                </Button>
            </Col>
            <Modal
                enforceFocus={false}
                show={showModal}
                onHide={() => setShowModal(false)}
                className="measurement-point-note-modal"
                contentClassName="measurement-point-note-content"
                title={meta.t(meta.label)}
                container={isMobile && document.getElementById('two-pane-layout')}
                footer={footer}
            >
                <form className="beacon-form">
                    {!meta.isHistoricalNote && (
                        <Row style={{ margin: '0px 5px' }}>
                            <FormGenerator onMount={onMount} fieldConfig={fieldConfig} />
                        </Row>
                    )}
                    <Row style={{ margin: '0px 20px' }}>
                        <RTE content={content} setContent={setContent} readOnly={meta.isHistoricalNote === true} />
                    </Row>
                    {meta.measurementPointID && (
                        <Row style={{ margin: '0px 20px' }}>
                            <InlinePhotoControl
                                ref={photoRef}
                                jobID={selectedJob.id}
                                installBaseID={meta.installBaseID || ''}
                                measurementPointID={meta.measurementPointID}
                                readOnly={meta.isHistoricalNote === true}
                                t={meta.t}
                            />
                        </Row>
                    )}
                </form>
            </Modal>
        </>
    );
};

export default NewMPNoteButton;
